import { registerBlock, registerRelationship } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const eggFreezing: ContentBlock<"program"> = {
  id: makeId("program-egg-freezing"),
  kind: "program",
  tags: ["egg-freezing", "her", "treatment", "pre-cycle", "during-cycle"],
  title: "Egg Freezing",
  subtitle:
    "Preserve your eggs — from assessment through retrieval and storage",
  icon: "❄️",
  body: {
    duration: "2 weeks – 4 months",
    includes: [
      "Initial fertility assessment (AMH, AFC, hormone panel)",
      "Optional 90-day egg quality optimization",
      "Clinic matching based on your location and priorities",
      "Ovarian stimulation with monitoring",
      "Egg retrieval procedure",
      "Cryopreservation and annual storage",
      "Medication cost comparison and financing guidance",
    ],
    priceRange: { low: 8000, high: 15000 },
    currency: "USD",
    status: "coming-soon",
    forWhom: "her",
    category: "treatment",
    customizable: true,
    moduleIds: [
      makeId("module-female-hormone-baseline"),
      makeId("module-female-optimization"),
      makeId("module-ovarian-stimulation"),
      makeId("module-egg-retrieval"),
    ],
    optionalModuleIds: [makeId("module-female-optimization")],
    phases: [
      {
        title: "Assessment",
        description: "Understand your fertility baseline",
        moduleIds: [makeId("module-female-hormone-baseline")],
      },
      {
        title: "Optimize (optional)",
        description: "90 days of supplements and lifestyle changes",
        moduleIds: [makeId("module-female-optimization")],
      },
      {
        title: "Stimulation",
        description: "10–14 days of daily injections with monitoring",
        moduleIds: [makeId("module-ovarian-stimulation")],
      },
      {
        title: "Retrieval & Storage",
        description: "Egg retrieval followed by cryopreservation",
        moduleIds: [makeId("module-egg-retrieval")],
      },
    ],
    steps: [
      {
        title: "Assessment",
        description:
          "Fertility labs and ultrasound to understand your starting point and plan your cycle.",
      },
      {
        title: "Optimize (optional)",
        description:
          "8–12 weeks of targeted supplements and lifestyle changes before stimulation.",
      },
      {
        title: "Stimulation",
        description:
          "10–14 days of daily injections with monitoring every 2–3 days.",
      },
      {
        title: "Retrieval & Storage",
        description:
          "Egg retrieval (~20 min), cryopreservation, and annual storage.",
      },
    ],
  },
  sources: [
    {
      title: "Planned oocyte cryopreservation outcomes and predictors",
      journal: "Fertility and Sterility",
      year: 2023,
      evidenceLevel: "cohort",
    },
  ],
};

registerBlock(eggFreezing);

registerRelationship({
  from: makeId("program-egg-freezing"),
  to: makeId("module-female-hormone-baseline"),
  type: "contains",
});
registerRelationship({
  from: makeId("program-egg-freezing"),
  to: makeId("module-female-optimization"),
  type: "contains",
});
registerRelationship({
  from: makeId("program-egg-freezing"),
  to: makeId("module-ovarian-stimulation"),
  type: "contains",
});
registerRelationship({
  from: makeId("program-egg-freezing"),
  to: makeId("module-egg-retrieval"),
  type: "contains",
});
